if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', class StickyAddToCart extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.section;
      this.realPicker = document.querySelector(
        `variant-radios[data-section="${this.sectionId}"], variant-selects[data-section="${this.sectionId}"]`
      );
      this.realButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      this.realPrice = document.getElementById(`price-${this.sectionId}`);
      this.stickyPrice = this.querySelector('[data-sticky-price]');
      this.stickyButton = this.querySelector('[data-sticky-submit]');
      // External-ticket events (buy-buttons.liquid) render a "BUY TICKETS" link instead
      // of an add-to-cart form; the sticky bar mirrors it.
      this.realTicketLink = document.getElementById('custom_rfsn_link');
      this.stickyTicketLink = this.querySelector('[data-sticky-ticket-link]');
    }

    connectedCallback() {
      this.bindOptionSelects();
      this.syncOptionsFromReal();
      this.mirrorTicketLink();
      this.observeRealButton();
      this.observeVisibility();

      // Keeps the sticky price/options in sync after the real picker's fetch-based re-render.
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (!event.data || event.data.sectionId !== this.sectionId) return;
        if (this.stickyPrice && this.realPrice) this.stickyPrice.innerHTML = this.realPrice.innerHTML;
        this.syncOptionsFromReal();
      });
    }

    // Reflects the real picker's current selection back into the sticky <select>s so the
    // bar always shows the variant that will actually be added — e.g. when the shopper
    // changes an option in the real picker below rather than in the sticky bar.
    syncOptionsFromReal() {
      if (!this.realPicker) return;

      const isDropdown = this.realPicker.tagName === 'VARIANT-SELECTS';
      const realInputGroups = isDropdown
        ? this.realPicker.querySelectorAll('select')
        : this.realPicker.querySelectorAll('fieldset');

      this.querySelectorAll('[data-sticky-option]').forEach((select, index) => {
        const source = realInputGroups[index];
        if (!source) return;
        const value = isDropdown ? source.value : source.querySelector('input:checked')?.value;
        if (value != null && select.value !== value) select.value = value;
      });
    }

    disconnectedCallback() {
      if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      if (this.buttonObserver) this.buttonObserver.disconnect();
      if (this.visibilityObserver) this.visibilityObserver.disconnect();
    }

    // Forward each sticky <select> change into the real variant picker so the
    // existing variant-radios/variant-selects component stays the single source
    // of truth for price, media, url, and cart-form updates.
    bindOptionSelects() {
      if (!this.realPicker) return;

      const isDropdown = this.realPicker.tagName === 'VARIANT-SELECTS';
      const realInputGroups = isDropdown
        ? this.realPicker.querySelectorAll('select')
        : this.realPicker.querySelectorAll('fieldset');

      this.querySelectorAll('[data-sticky-option]').forEach((select, index) => {
        const target = realInputGroups[index];
        if (!target) return;

        select.addEventListener('change', () => {
          if (isDropdown) {
            target.value = select.value;
            target.dispatchEvent(new Event('change', { bubbles: true }));
            return;
          }

          const input = target.querySelector(`input[value="${CSS.escape(select.value)}"]`);
          if (!input) return;
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }

    // External-ticket events: point the sticky "BUY TICKETS" link at the same URL as the
    // real one, copying it again on click so any referral (rfsn) href rewrite carries over.
    mirrorTicketLink() {
      if (!this.stickyTicketLink || !this.realTicketLink) return;
      const apply = () => {
        this.stickyTicketLink.href = this.realTicketLink.href;
      };
      apply();
      this.stickyTicketLink.addEventListener('click', apply);
    }

    // Mirrors the real add-to-cart button's disabled/loading/label state so the
    // sticky button behaves identically without re-implementing product-form.js.
    observeRealButton() {
      if (!this.realButton || !this.stickyButton) return;

      const sync = () => {
        this.stickyButton.disabled = this.realButton.disabled;
        const ariaDisabled = this.realButton.getAttribute('aria-disabled');
        if (ariaDisabled) {
          this.stickyButton.setAttribute('aria-disabled', ariaDisabled);
        } else {
          this.stickyButton.removeAttribute('aria-disabled');
        }
        this.stickyButton.classList.toggle('loading', this.realButton.classList.contains('loading'));

        const realLabel = this.realButton.querySelector('span');
        const stickyLabel = this.stickyButton.querySelector('span');
        if (realLabel && stickyLabel) stickyLabel.textContent = realLabel.textContent;
      };

      sync();
      this.buttonObserver = new MutationObserver(sync);
      this.buttonObserver.observe(this.realButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class'],
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    // Shows the bar once the real CTA (add-to-cart button, or the BUY TICKETS link on
    // external-ticket events) has scrolled above the viewport.
    observeVisibility() {
      const anchor = this.realButton || this.realTicketLink;
      if (!anchor) {
        this.classList.add('sticky-atc--visible');
        return;
      }

      this.visibilityObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.classList.toggle('sticky-atc--visible', scrolledPast);
          });
        },
        { threshold: 0 }
      );
      this.visibilityObserver.observe(anchor);
    }
  });
}
